import {
  ArrowLeft,
  Box,
  Check,
  Clipboard,
  Code2,
  Download,
  Eye,
  Heart,
  Share2,
  Sparkles,
  Terminal,
} from "lucide-react";
import { useMemo, useState } from "react";
import { STANDARD_STATE_SPECS, type StandardState } from "codexpetlab-core";
import { DirectionPlayground, SpritePlayer, downloadBlob, exportStateGif } from "codexpetlab-player";
import type { Pet } from "../data/pets";
import { createPetManifest, createValidationReport, petDownloadPath, petRowCount } from "../lib/pet-contract";
import { petCliCommand, petCodexInstallUrl, petCurlCommand } from "../lib/install";
import { PET_STATE_NAMES } from "../lib/sprite-atlas";

interface PetDetailPageProps {
  pet: Pet;
  liked: boolean;
  viewed: boolean;
  onBack: () => void;
  onLike: () => void;
  onShare: () => void;
}

function formatCount(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}k`;
  return value.toString();
}

export function PetDetailPage({ pet, liked, viewed, onBack, onLike, onShare }: PetDetailPageProps) {
  const [stateIndex, setStateIndex] = useState(0);
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  const [exportingGif, setExportingGif] = useState(false);
  const [gifStatus, setGifStatus] = useState<string | null>(null);
  const [installMode, setInstallMode] = useState<"cli" | "curl">("cli");
  const [copied, setCopied] = useState(false);
  const states = PET_STATE_NAMES.slice(0, petRowCount(pet));
  const validation = createValidationReport(pet);
  const manifestBytes = useMemo(
    () => new TextEncoder().encode(`${JSON.stringify(createPetManifest(pet), null, 2)}\n`).byteLength,
    [pet],
  );
  const command = installMode === "cli" ? petCliCommand(pet) : petCurlCommand(pet);
  const spritesheetUrl = `/api/pets/${pet.id}/spritesheet`;
  const selectedState = STANDARD_STATE_SPECS[Math.min(stateIndex, 8)]?.key ?? "idle";

  const copyCommand = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const exportGif = async () => {
    setExportingGif(true);
    setGifStatus(null);
    try {
      const blob = await exportStateGif({ src: spritesheetUrl, state: selectedState as StandardState });
      const signature = new TextDecoder().decode(new Uint8Array(await blob.slice(0, 6).arrayBuffer()));
      if (signature !== "GIF89a") throw new Error(`Unexpected GIF signature: ${signature}`);
      downloadBlob(blob, `${pet.id}-${selectedState}.gif`);
      setGifStatus(`Saved ${(blob.size / 1024).toFixed(1)} KB GIF`);
    } catch (error) {
      setGifStatus(error instanceof Error ? error.message : "GIF export failed");
    } finally {
      setExportingGif(false);
    }
  };

  return (
    <section className="detail-page">
      <button className="detail-back" type="button" onClick={onBack}>
        <ArrowLeft size={17} /> Back to gallery
      </button>

      <div className="detail-layout">
        <article className="detail-main-card">
          <p className="detail-id"><span /> id / <strong>{pet.id}</strong> · by <strong>{pet.creatorHandle}</strong></p>
          <h1>{pet.name}</h1>
          <p className="detail-description">{pet.description}</p>

          <div className="detail-stage">
            <button className={playgroundOpen ? "direction-button active" : "direction-button"} type="button" onClick={() => setPlaygroundOpen((open) => !open)}>
              <Sparkles size={15} /> Try its 16 look directions
            </button>
            {playgroundOpen ? (
              <DirectionPlayground
                src={spritesheetUrl}
                width={430}
                className="detail-sprite-canvas"
                aria-label={`${pet.name} 16-direction playground`}
              />
            ) : (
              <SpritePlayer
                src={spritesheetUrl}
                state={selectedState as StandardState}
                width={430}
                className="detail-sprite-canvas"
                aria-label={`${pet.name} ${states[stateIndex]} animation`}
              />
            )}
            <div className="state-tabs" role="tablist" aria-label={`${pet.name} animation states`}>
              {states.map((state, index) => (
                <button
                  key={state}
                  className={index === stateIndex ? "active" : ""}
                  role="tab"
                  aria-selected={index === stateIndex}
                  type="button"
                  onClick={() => { setStateIndex(index); if (index < 9) setPlaygroundOpen(false); else setPlaygroundOpen(true); }}
                >
                  {state}
                </button>
              ))}
            </div>
          </div>

          <div className="detail-social-row">
            <div className="detail-stats">
              <span><Eye size={15} /> {formatCount(pet.views + (viewed ? 1 : 0))}</span>
              <span><Heart size={15} /> {formatCount(pet.likes + (liked ? 1 : 0))}</span>
              <span><Download size={15} /> {formatCount(pet.downloads)}</span>
            </div>
            <div className="detail-actions">
              <button className={liked ? "secondary-button liked" : "secondary-button"} type="button" onClick={onLike}>
                <Heart size={16} fill={liked ? "currentColor" : "none"} /> Like
              </button>
              <button className="secondary-button" type="button" onClick={onShare}><Share2 size={16} /> Share</button>
              <button className="secondary-button" type="button" onClick={() => void exportGif()} disabled={exportingGif || playgroundOpen}>
                <Download size={16} /> {exportingGif ? "Exporting…" : `Export ${selectedState} GIF`}
              </button>
              {gifStatus && <span className="gif-status" role="status">{gifStatus}</span>}
            </div>
          </div>
        </article>

        <aside className="install-card">
          <div className="install-heading"><span>CODEX INSTALL</span><span>Ask Codex to install this pet.</span></div>
          <a className="install-codex-button" href={petCodexInstallUrl(pet)}>
            <Terminal size={18} /> Install in Codex
          </a>

          <p className="install-label">Terminal install command</p>
          <div className="command-card">
            <div className="command-tabs">
              <button className={installMode === "cli" ? "active" : ""} type="button" onClick={() => setInstallMode("cli")}>CLI</button>
              <button className={installMode === "curl" ? "active" : ""} type="button" onClick={() => setInstallMode("curl")}>curl</button>
            </div>
            {installMode === "curl" && <span className="command-destination">Destination: $HOME/.codex/pets/{pet.id}</span>}
            <pre><code><span>$</span> {command}</code></pre>
            <button className="copy-command" type="button" onClick={() => void copyCommand()}>
              {copied ? <Check size={15} /> : <Clipboard size={15} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <a className="sprite-download-link" href={petDownloadPath(pet)} download={`${pet.id}.codex-pet.zip`}>
            <Box size={17} /> Download sprite kit <strong>.codex-pet.zip</strong>
          </a>

          <div className="manifest-grid">
            <div><span>Manifest</span><strong>{pet.id}</strong></div>
            <div><span>Atlas</span><strong>{validation.atlasSize}</strong></div>
            <div><span>Rows</span><strong>{validation.statesDetected}</strong></div>
            <div><span>pet.json</span><strong>{manifestBytes} B</strong></div>
            <div><span>Cell</span><strong>{validation.cellSize}</strong></div>
            <div><span>Directions</span><strong>16</strong></div>
          </div>

          <div className="compatibility-note">
            <Code2 size={17} />
            <p>Uses the first-party <code>codexpetlab</code> installer. The existing <code>codex-pets</code> CLI remains compatible through <code>CODEX_PETS_API_BASE</code>.</p>
          </div>
        </aside>
      </div>
    </section>
  );
}
